import React, { useState, useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import styled from 'styled-components';

const convertQuillClassesToInlineStyles = (html: string): string => {
  return html
    .replace(/class="ql-align-center"/g, 'style="text-align: center;"')
    .replace(/class="ql-align-right"/g, 'style="text-align: right;"')
    .replace(/class="ql-align-justify"/g, 'style="text-align: justify;"')
    .replace(/class="ql-indent-(\d+)"/g, (_, level) => `style="padding-left: ${Number(level) * 3}em;"`);
};

const StyledQuillContainer = styled.div<{ $minHeight?: number }>`
  .ql-color .ql-picker-options {
    width: 112px !important;
  }

  ${({ $minHeight }) =>
    $minHeight &&
    `
    .ql-editor {
      min-height: ${$minHeight}px;
    }
  `}
`;

export const EditorQuill = ({
  text,
  onChange,
  placeholder,
  className,
  minHeight,
  enableLinks,
}: {
  text: string;
  onChange: (text: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  enableLinks?: boolean;
}) => {
  // Manage state internally to prevent cursor jumping
  const [internalValue, setInternalValue] = useState(text);
  const isInternalChange = useRef(false);
  const quillRootRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  const toolbar = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic'],
    ...(enableLinks ? [['link']] : []),
    [
      {
        color: [
          '#413E3B',
          '#A08264',
          '#70D6BA',
          '#F49C20',
          '#D4244E',
          '#968F8B',
          '#D9CDC1',
          '#C6EFE3',
          '#FAD03E',
          '#FD4B4C',
        ],
      },
    ],
    [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ];

  const clipboard = {
    matchVisual: false,
  };

  useEffect(() => {
    if (!quillRootRef.current || quillRef.current) {
      return;
    }

    const quill = new Quill(quillRootRef.current, {
      theme: 'snow',
      placeholder,
      modules: {
        toolbar,
        clipboard,
      },
    });

    quillRef.current = quill;

    if (text) {
      quill.clipboard.dangerouslyPasteHTML(text, 'silent');
    } else {
      quill.setText('', 'silent');
    }

    const handleTextChange = (_delta, _oldDelta, source) => {
      if (source !== 'user') {
        return;
      }

      const currentHtml = quill.root.innerHTML;
      isInternalChange.current = true;
      setInternalValue(currentHtml);
      onChange(convertQuillClassesToInlineStyles(currentHtml));
    };

    quill.on('text-change', handleTextChange);

    return () => {
      quill.off('text-change', handleTextChange);
      quillRef.current = null;
    };
  }, []);

  // Only sync external changes when the text prop changes from outside.
  useEffect(() => {
    if (!quillRef.current) {
      return;
    }

    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    if (text !== internalValue) {
      if (text) {
        quillRef.current.clipboard.dangerouslyPasteHTML(text, 'silent');
      } else {
        quillRef.current.setText('', 'silent');
      }
      setInternalValue(text);
    }
  }, [text]);

  return (
    <StyledQuillContainer $minHeight={minHeight} className={className}>
      <div ref={quillRootRef} />
    </StyledQuillContainer>
  );
};
